// Trade Flow Tracking Admin API Client

// Base path: /api/admin/trade-flow

// NOTE: apiClient (Axios) already prepends /api, so paths are relative to that.



// ---------------------------------------------------------------------------

// Shared types

// ---------------------------------------------------------------------------



export interface PaginatedResponse<T> {

  data: T[];

  total: number;

  page: number;

  page_size: number;

}



// ---------------------------------------------------------------------------

// Tab 1: Platform Fetch

// ---------------------------------------------------------------------------



export interface FetchSession {

  session_id: string;

  fetched_at: string | null;

  provider: string;

  symbols: string[];

  pair_count: number;

}



export interface FetchSessionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 2: Genesis Filters

// ---------------------------------------------------------------------------



export interface GenesisDecision {

  id: string;

  recommendation_id: string;

  symbol: string;

  filter_name: string;

  passed: boolean;

  actual_value: number | null;

  threshold_low: number | null;

  threshold_high: number | null;

  detail: string | null;

  evaluated_at: string | null;

}



export interface GenesisDecisionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  filter_name?: string;

  passed?: boolean;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 3: Pipeline Filters

// ---------------------------------------------------------------------------



export interface PipelineDecision {

  id: string;

  recommendation_id: string;

  symbol: string;

  filter_name: string;

  filter_sub_name: string | null;

  passed: boolean;

  actual_value: number | null;

  threshold_low: number | null;

  threshold_high: number | null;

  detail: string | null;

  overall_score: number | null;

  direction: string | null;

  evaluated_at: string | null;

}



export interface PipelineDecisionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  recommendation_id?: string;

  filter_name?: string;

  passed?: boolean;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 4: XGBoost

// ---------------------------------------------------------------------------



export interface XGBoostDecision {

  id: string;

  recommendation_id: string | null;

  symbol: string;

  stage: string | null;

  score: number | null;

  threshold: number | null;

  passed: boolean | null;

  reason: string | null;

  model_version: string | null;

  created_at: string | null;

}



export interface XGBoostDecisionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  passed?: boolean;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 5: Scoring & Gates (coming from filter_decisions scoring category)

// ---------------------------------------------------------------------------



export interface ScoringGateDecisionsParams {
  page?: number;
  page_size?: number;
  date_from?: string;
  date_to?: string;
  symbol?: string;
  recommendation_id?: string;
  passed?: boolean;
  sort_by?: string;
  sort_order?: string;
}

export interface ScoringGateDecision {
  id: string;
  recommendation_id: string;
  symbol: string;
  filter_name: string;
  passed: boolean;
  actual_value: number | null;
  threshold_low: number | null;
  threshold_high: number | null;
  overall_score: number | null;
  detail: string | null;
  evaluated_at: string | null;
}



// ---------------------------------------------------------------------------

// Tab 6: Smart Pool

// ---------------------------------------------------------------------------



export interface SmartPoolEntry {

  recommendation_id: string;

  symbol: string;

  score: number;

  confidence: number | null;

  entry: number | null;

  stop_loss: number | null;

  take_profit: number[] | null;

  direction: string | null;

  created_at: string | null;

}



// ---------------------------------------------------------------------------

// Tab 7: Active + Reserve Pools

// ---------------------------------------------------------------------------



export interface PoolEntry {

  recommendation_id: string;

  symbol: string;

  score: number;

  confidence: number | null;

  entry: number | null;

  stop_loss: number | null;

  take_profit: number[] | null;

  direction: string | null;

  created_at: string | null;

}



export interface PoolsResponse {

  active: PoolEntry[];

  reserve: PoolEntry[];

  total_active: number;

  total_reserve: number;

}



// ---------------------------------------------------------------------------

// Tab 8: Live Entry Gate v2

// ---------------------------------------------------------------------------



export interface LiveGateDecision {

  id: string;

  recommendation_id: string;

  symbol: string;

  filter_name: string;

  passed: boolean;

  actual_value: number | null;

  threshold_low: number | null;

  threshold_high: number | null;

  detail: string | null;

  evaluated_at: string | null;

}



export interface LiveGateDecisionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  recommendation_id?: string;

  filter_name?: string;

  passed?: boolean;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 9: Enhanced Technical Gate

// ---------------------------------------------------------------------------



export interface TechnicalGateDecision {

  id: string;

  recommendation_id: string;

  symbol: string;

  indicator: string;

  value: number | null;

  signal: string | null;

  threshold: number | null;

  passed: boolean;

  evaluated_at: string | null;

}



export interface TechnicalGateDecisionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  recommendation_id?: string;

  passed?: boolean;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 10: Smart Entry Sniper

// ---------------------------------------------------------------------------



export interface SniperSession {

  recommendation_id: string;

  symbol: string;

  decision: string | null;

  best_score: number | null;

  poll_count: number | null;

  adverse_move_pct: number | null;

  window_duration_seconds: number | null;

  evaluated_at: string | null;

}



export interface SniperSessionsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  recommendation_id?: string;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Tab 11: Execution

// ---------------------------------------------------------------------------



export interface ExecutionJobSummary {

  id: string;

  recommendation_id: string | null;

  symbol: string | null;

  job_type: string | null;

  status: string | null;

  entry_price: number | null;

  exit_price: number | null;

  pnl_pct: number | null;

  exit_reason: string | null;

  duration_seconds: number | null;

  attempt_count: number;

  error_message: string | null;

  created_at: string | null;

}



export interface ExecutionJobsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  job_type?: string;

  status?: string;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Pool Movements

// ---------------------------------------------------------------------------



export interface PoolMovement {

  id: string;

  event_type: string;

  entity_id: string;

  symbol: string;

  decision_reason: string;

  decision_source: string;

  pool_from: string | null;

  pool_to: string | null;

  pool_score: number | null;

  timestamp: string | null;

}



export interface PoolMovementsParams {

  page?: number;

  page_size?: number;

  date_from?: string;

  date_to?: string;

  symbol?: string;

  recommendation_id?: string;

  event_type?: string;

  sort_by?: string;

  sort_order?: string;

}



// ---------------------------------------------------------------------------

// Journey (Comprehensive symbol timeline)

// ---------------------------------------------------------------------------



export interface TradeJourneyStage {

  stage: string;

  stage_number: number;

  stage_label: string;

  passed: boolean | null;

  score: number | null;

  details: Array<Record<string, unknown>>;

}



export interface TradeJourney {

  recommendation_id: string;

  symbol: string;

  stages: TradeJourneyStage[];

}



export interface JourneyParams {

  date_from?: string;

  date_to?: string;

  limit?: number;

}



// ---------------------------------------------------------------------------

// API client (uses shared Axios instance with CSRF + cookie auth)

// ---------------------------------------------------------------------------



import { apiClient } from './apiClient';



const BASE_PATH = '/admin/trade-flow';



// ---------------------------------------------------------------------------

// Exported API functions (one per endpoint)

// ---------------------------------------------------------------------------



export async function getFetchSessions(

  params: FetchSessionsParams = {}

): Promise<PaginatedResponse<FetchSession>> {

  const { data } = await apiClient.get<PaginatedResponse<FetchSession>>(`${BASE_PATH}/fetch`, { params });

  return data;

}



export async function getGenesisDecisions(

  params: GenesisDecisionsParams = {}

): Promise<PaginatedResponse<GenesisDecision>> {

  const { data } = await apiClient.get<PaginatedResponse<GenesisDecision>>(`${BASE_PATH}/genesis`, { params });

  return data;

}



export async function getPipelineDecisions(

  params: PipelineDecisionsParams = {}

): Promise<PaginatedResponse<PipelineDecision>> {

  const { data } = await apiClient.get<PaginatedResponse<PipelineDecision>>(`${BASE_PATH}/pipeline`, { params });

  return data;

}



export async function getXGBoostDecisions(

  params: XGBoostDecisionsParams = {}

): Promise<PaginatedResponse<XGBoostDecision>> {

  const { data } = await apiClient.get<PaginatedResponse<XGBoostDecision>>(`${BASE_PATH}/xgboost`, { params });

  return data;

}

export async function getScoringDecisions(
  params: ScoringGateDecisionsParams = {}
): Promise<PaginatedResponse<ScoringGateDecision>> {
  const { data } = await apiClient.get<PaginatedResponse<ScoringGateDecision>>(`${BASE_PATH}/scoring`, { params });
  return data;
}

export async function getSmartPool(): Promise<{ data: SmartPoolEntry[]; total: number; updated_at: string }> {

  const { data } = await apiClient.get<{ data: SmartPoolEntry[]; total: number; updated_at: string }>(`${BASE_PATH}/smart-pool`);

  return data;

}



export async function getPools(): Promise<PoolsResponse> {

  const { data } = await apiClient.get<PoolsResponse>(`${BASE_PATH}/pools`);

  return data;

}



export async function getLiveGateDecisions(

  params: LiveGateDecisionsParams = {}

): Promise<PaginatedResponse<LiveGateDecision>> {

  const { data } = await apiClient.get<PaginatedResponse<LiveGateDecision>>(`${BASE_PATH}/live-gate`, { params });

  return data;

}



export async function getTechnicalGateDecisions(

  params: TechnicalGateDecisionsParams = {}

): Promise<PaginatedResponse<TechnicalGateDecision>> {

  const { data } = await apiClient.get<PaginatedResponse<TechnicalGateDecision>>(`${BASE_PATH}/technical`, { params });

  return data;

}



export async function getSniperSessions(

  params: SniperSessionsParams = {}

): Promise<PaginatedResponse<SniperSession>> {

  const { data } = await apiClient.get<PaginatedResponse<SniperSession>>(`${BASE_PATH}/sniper`, { params });

  return data;

}



export async function getExecutionJobs(

  params: ExecutionJobsParams = {}

): Promise<PaginatedResponse<ExecutionJobSummary>> {

  const { data } = await apiClient.get<PaginatedResponse<ExecutionJobSummary>>(`${BASE_PATH}/execution`, { params });

  return data;

}



export async function getSymbolJourney(

  symbol: string,

  params: JourneyParams = {}

): Promise<{ data: TradeJourney[] }> {

  const encodedSymbol = encodeURIComponent(symbol);

  const { data } = await apiClient.get<{ data: TradeJourney[] }>(

    `${BASE_PATH}/journey/${encodedSymbol}`,

    { params }

  );

  return data;

}



export async function getPoolMovements(

  params: PoolMovementsParams = {}

): Promise<PaginatedResponse<PoolMovement>> {

  const { data } = await apiClient.get<PaginatedResponse<PoolMovement>>(`${BASE_PATH}/pool-movements`, { params });

  return data;

}

